import { LDR_PARTS } from "./ldrExport";

export interface BomCsvRow {
  id: string;
  name: string;
  colorName: string;
  quantity: number;
  unitPrice: number;
}

function escapeCsvField(value: string | number): string {
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function bomDataToCsv(inventory: BomCsvRow[]): string {
  const header =
    "Part ID,Part Name,Color,Quantity,Unit Price (USD),Line Total (USD)";

  const rows = inventory.map((row) =>
    [
      escapeCsvField(row.id),
      escapeCsvField(row.name),
      escapeCsvField(row.colorName),
      row.quantity,
      row.unitPrice.toFixed(2),
      (row.unitPrice * row.quantity).toFixed(2),
    ].join(","),
  );

  return [header, ...rows].join("\r\n");
}

export function exportBomCsv(
  inventory: BomCsvRow[],
  filename = "bricksup-bom.csv",
): void {
  const csv = bomDataToCsv(inventory);
  const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}

// BrickLink color IDs for the 21-color palette, keyed by LEGO element name
// (verified against a cross-platform color guide, 2026-09-27).
const BL_COLOR_IDS: Record<string, number> = {
  White: 1,
  "Dark Stone Gray": 85,
  "Bright Red": 5,
  "Dark Green": 80,
  "Earth Blue": 63,
  "Bright Yellow": 3,
  "Flame Orangey Yellow": 110,
  "Medium Lavender": 157,
  "Reddish Brown": 88,
  "Light Stone Gray": 99,
  "Medium Stone Gray": 86,
  "Cool Yellow": 103,
  Silver: 95,
  Orange: 4,
  "Sand Blue": 55,
  "Sand Yellow": 69,
  "Medium Azur": 156,
  "Bright Green": 36,
  "Bright Yellowish Green": 34,
  "Earth Green": 80,
  Nougat: 28,
};

// BrickLink Wanted-List upload format: ITEMTYPE (P = part), ITEMID (design ID,
// same table as the LDraw exporter), COLOR (BrickLink color ID), MINQTY.
// Unknown footprints fall back to 1x1 bricks (3005), unknown colors to White (1).
export function bomDataToBrickLinkCsv(inventory: BomCsvRow[]): string {
  const header = "ITEMTYPE,ITEMID,COLOR,MINQTY";
  const rows = inventory.map((row) =>
    [
      "P",
      LDR_PARTS[row.id] ?? "3005",
      BL_COLOR_IDS[row.colorName] ?? 1,
      row.quantity,
    ].join(","),
  );
  return [header, ...rows].join("\r\n");
}

export function exportBomBrickLinkCsv(
  inventory: BomCsvRow[],
  filename = "bricksup-bricklink-wanted.csv",
): void {
  const csv = bomDataToBrickLinkCsv(inventory);
  const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}

// Rebrickable color IDs for the 21-color palette, keyed by LEGO element name
// (verified against a cross-platform color guide, 2026-09-27).
const RB_COLOR_IDS: Record<string, number> = {
  White: 15,
  "Dark Stone Gray": 72,
  "Bright Red": 4,
  "Dark Green": 288,
  "Earth Blue": 272,
  "Bright Yellow": 14,
  "Flame Orangey Yellow": 191,
  "Medium Lavender": 30,
  "Reddish Brown": 70,
  "Light Stone Gray": 151,
  "Medium Stone Gray": 71,
  "Cool Yellow": 226,
  Silver: 179,
  Orange: 25,
  "Sand Blue": 379,
  "Sand Yellow": 28,
  "Medium Azur": 322,
  "Bright Green": 10,
  "Bright Yellowish Green": 27,
  "Earth Green": 288,
  Nougat: 92,
};

// Rebrickable part-list import format: `Part,Color,Quantity` with design IDs
// and Rebrickable color IDs (verified via rebrickable.com/help/lists-parts).
// Unknown footprints fall back to 1x1 bricks (3005), unknown colors to White (15).
export function bomDataToRebrickableCsv(inventory: BomCsvRow[]): string {
  const header = "Part,Color,Quantity";
  const rows = inventory.map((row) =>
    [
      LDR_PARTS[row.id] ?? "3005",
      RB_COLOR_IDS[row.colorName] ?? 15,
      row.quantity,
    ].join(","),
  );
  return [header, ...rows].join("\r\n");
}

export function exportBomRebrickableCsv(
  inventory: BomCsvRow[],
  filename = "bricksup-rebrickable-parts.csv",
): void {
  const csv = bomDataToRebrickableCsv(inventory);
  const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}
